import { RefObject, useEffect, useState } from 'react';

type Args = {
  containerRef: RefObject<HTMLElement | null>;
  chipNodesRef: RefObject<Array<HTMLDivElement | null>>;
  selectedOptionsLength: number;
  direction?: 'horizontal' | 'vertical';
  lockActiveState: boolean;
  threshold?: number;
};

const useVerticalInputRowFit = ({
  containerRef,
  chipNodesRef,
  selectedOptionsLength,
  direction,
  lockActiveState,
  threshold = 0.75,
}: Args) => {
  const [needsRowBreak, setNeedsRowBreak] = useState(false);

  useEffect(() => {
    if (direction !== 'vertical' || !lockActiveState || selectedOptionsLength === 0) {
      setNeedsRowBreak(false);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const compute = () => {
      const chips = chipNodesRef.current?.filter(Boolean) as HTMLDivElement[] | undefined;
      if (!chips?.length) {
        setNeedsRowBreak(false);
        return;
      }
      const last = chips[chips.length - 1];
      const lastTop = last.offsetTop;
      // collect all chips on the same visual row as the last chip
      let lastRowWidth = 0;
      for (let i = chips.length - 1; i >= 0; i--) {
        const c = chips[i];
        if (c.offsetTop !== lastTop) break;
        lastRowWidth += c.offsetWidth;
      }
      const cw = container.clientWidth || 1;
      setNeedsRowBreak(lastRowWidth / cw > threshold);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [containerRef, chipNodesRef, selectedOptionsLength, direction, lockActiveState, threshold]);

  return needsRowBreak;
};

export default useVerticalInputRowFit;
